import httpClient from './httpClient';
import { LONG_ORCHESTRATOR_REQUEST_MS } from './apiTimeouts';

export interface StartInterviewRequest {
  candidate_name?: string;
  interview_type: string;
  token: string;
  response_feedback?: boolean;
  difficulty?: string;
  experience_focus?: string;
  mode?: string;
}

export interface StartInterviewResponse {
  success: boolean;
  question: string;
  audio_url?: string;
  audio_mode?: string;
  message?: string;
  session_id?: string;
  /** Planned question cap for this session (from backend interview config / round). */
  max_questions?: number;
  token_info?: {
    token: string;
    tokens_consumed: number;
    remaining_quota: number | string;
  };
}

export interface WebSocketMessage {
  type: string;
  text?: string;
  question?: string;
  audio_url?: string;
  silence_duration?: number;
  silence_threshold?: number;
  transcribed_text?: string;
  question_number?: number;
  message?: string;
  interview_complete?: boolean;
  hide_user_message?: boolean;
  feedback?: any;
  spoken_feedback?: {
    spoken_text: string;
    audio_url: string;
    audio_file: string;
  };
  total_questions?: number;
  max_questions?: number;
  responses?: any[];
}

export const interviewApi = {
  // Validate interview token
  validateToken: async (token: string) => {
    const response = await httpClient.post('/validate-token', { token });
    return response.data;
  },

  // Start a new interview session via orchestrator (context-first)
  startInterview: async (data: StartInterviewRequest): Promise<StartInterviewResponse> => {
    const response = await httpClient.post('/orchestrator/interview/start', {
      interview_type: data.interview_type,
      candidate_name: data.candidate_name,
      token: data.token,
      response_feedback: data.response_feedback ?? true,
      difficulty: data.difficulty,
      experience_focus: data.experience_focus,
      mode: data.mode,
    }, { timeout: LONG_ORCHESTRATOR_REQUEST_MS });
    return response.data;
  },

  // Get interview status
  getInterviewStatus: async (sessionId: string) => {
    const response = await httpClient.get(`/interview-status/${sessionId}`);
    return response.data;
  },

  // Submit transcription
  submitTranscription: async (sessionId: string, transcription: string, questionNumber: number) => {
    const response = await httpClient.post('/submit-transcription', {
      session_id: sessionId,
      transcription_text: transcription,
      question_number: questionNumber,
    });
    return response.data;
  },

  // End interview
  endInterview: async (sessionId: string) => {
    const response = await httpClient.post('/end-interview', {
      session_id: sessionId,
    });
    return response.data;
  },

  // Get interview feedback (legacy — kept for backward compat)
  getFeedback: async (sessionId: string) => {
    const response = await httpClient.get(`/feedback/${sessionId}`);
    return response.data;
  },

  /**
   * Fetch full session feedback via the orchestrator.
   * Checks context-service (new flow) first, then falls back to legacy sessions table.
   */
  getSessionFeedback: async (sessionId: string) => {
    const response = await httpClient.get(
      `orchestrator/session/feedback/${encodeURIComponent(sessionId)}`
    );
    return response.data;
  },
};

export default interviewApi;
